import React, { useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, DollarSign, Info, Link as LinkIcon, RefreshCw, Settings2, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import {
  useTrailLeakage,
  TrailLeakageStatusFilter,
  TrailLeakageAlert,
  TrailLeakageAlertStatus,
} from '../../hooks/useTrailLeakage';
import { useCrmMappingCoverage } from '../../hooks/useCrmMappingCoverage';

// Permitted next statuses per current status (mirrors the backend state
// machine in updateTrailLeakageAlertStatus.ts). A status never transitions to
// itself.
const NEXT_STATUS_OPTIONS: Record<TrailLeakageAlertStatus, TrailLeakageAlertStatus[]> = {
  open: ['investigating', 'resolved', 'false_positive'],
  investigating: ['open', 'resolved', 'false_positive'],
  resolved: ['open', 'investigating', 'false_positive'],
  false_positive: ['open', 'investigating', 'resolved'],
};

const STATUS_LABEL: Record<TrailLeakageAlertStatus, string> = {
  open: 'Open',
  investigating: 'Investigating',
  resolved: 'Resolved',
  false_positive: 'False positive',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  open: 'bg-red-500/15 text-red-300',
  investigating: 'bg-amber-500/15 text-amber-300',
  resolved: 'bg-emerald-500/15 text-emerald-300',
  false_positive: 'bg-gray-700 text-gray-300',
};

type SortKey = 'borrower' | 'lender' | 'broker' | 'office' | 'loanValue' | 'settled' | 'monthsSince';

// G2 locked-definition explainer banner. The text MUST stay in sync with the
// SQL gates in services/client-functions/src/crm/trailLeakageDetector.ts
// (crm_settled CTE) — both reference plan-id G2. Moved here from the former
// broker-activity wrapper when Trail Leakage returned to /reports/trail-leakage.
const TrailLeakageDefinitionBanner: React.FC = () => (
  <div
    role="note"
    aria-label="Trail leakage definition"
    className="rounded-md border border-blue-700/50 bg-blue-900/20 p-4 text-sm text-blue-200 flex items-start gap-3"
  >
    <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-300" aria-hidden="true" />
    <p className="leading-relaxed">
      A &lsquo;leaking&rsquo; loan settled <strong>between 60 days and 6 years ago</strong>, is{' '}
      <strong>not in arrears or suspended</strong>, and has{' '}
      <strong>no trail commission from that lender across the recent commission-payment months</strong>{' '}
      (a rolling six-month window, matched by broker and lender).
    </p>
  </div>
);

const STATUS_TABS: Array<{ key: TrailLeakageStatusFilter; label: string }> = [
  { key: 'open', label: 'Open' },
  { key: 'investigating', label: 'Investigating' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'false_positive', label: 'False Positive' },
  { key: 'all', label: 'All' },
];

function formatCurrency(cents: number | null): string {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  });
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return value;
  }
}

interface MappingCoverageEmptyStateProps {
  companyId: string;
  status: TrailLeakageStatusFilter;
}

const MappingCoverageEmptyState: React.FC<MappingCoverageEmptyStateProps> = ({ companyId, status }) => {
  const { coverage, loading } = useCrmMappingCoverage(companyId);

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-10 text-center text-gray-400">
        <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
        Checking mapping coverage…
      </div>
    );
  }

  if (status !== 'open') {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-10 text-center text-gray-400">
        No alerts with this status.
      </div>
    );
  }

  const advisersTotal = coverage?.advisersTotal ?? 0;
  const advisersMapped = coverage?.advisersMapped ?? 0;
  const lendersObserved = coverage?.lendersObserved ?? 0;
  const lendersMapped = coverage?.lendersMapped ?? 0;
  const advisersComplete = advisersTotal > 0 && advisersMapped >= advisersTotal;
  const lendersComplete = lendersObserved > 0 && lendersMapped >= lendersObserved;
  const fullyMapped = advisersComplete && lendersComplete;

  if (fullyMapped) {
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-10 text-center">
        <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
        <div className="text-base text-white font-semibold">Trail book fully reconciled</div>
        <p className="text-sm text-gray-400 mt-2">
          Every eligible deal in your CRM (settled between 60 days and 6 years ago) has a matching
          trail commission row. Mapping coverage is complete: {advisersMapped}/{advisersTotal} brokers,{' '}
          {lendersMapped}/{lendersObserved} lenders.
        </p>
      </div>
    );
  }

  // Pre-first-sync fallback — mapping tables empty, nothing observed yet.
  if (advisersTotal === 0 && lendersObserved === 0) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-10 text-center text-gray-400">
        No alerts yet. Your CRM sync has not completed its first run, or no settled deals have
        surfaced in the detector. Use "Sync now" on the Integrations page to trigger a run.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-white font-semibold">No alerts — but mapping coverage is incomplete</div>
          <p className="text-sm text-gray-300 mt-1">
            The detector dropped candidate deals because these reference tables aren't fully populated.
            Until they are, trail leakage can't be reliably detected.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className={`rounded-md border p-3 ${advisersComplete ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/10'}`}>
              <div className="text-xs uppercase tracking-wider text-gray-400">Brokers mapped</div>
              <div className="text-lg text-white mt-1 font-mono">
                {advisersMapped} / {advisersTotal}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Each CRM adviser classified as a Broker needs a VALUTTEN broker ID so the detector can
                cross-check commissions. Non-broker advisers are excluded from this count.
              </p>
            </div>
            <div className={`rounded-md border p-3 ${lendersComplete ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/10'}`}>
              <div className="text-xs uppercase tracking-wider text-gray-400">Lenders mapped</div>
              <div className="text-lg text-white mt-1 font-mono">
                {lendersMapped} / {lendersObserved}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Each CRM lender name needs a canonical mapping to the name in your commission data.
              </p>
            </div>
          </div>
          <Link
            to="/settings/crm-mappings"
            className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm"
          >
            <Settings2 className="w-4 h-4" />
            Open CRM Mappings
          </Link>
        </div>
      </div>
    </div>
  );
};

interface StatusActionCellProps {
  alert: TrailLeakageAlert;
  busy: boolean;
  onChange: (next: TrailLeakageAlertStatus) => void;
}

// Per-row status control: a badge for the current status plus a compact select
// listing only the permitted next statuses. Selecting one fires the mutation.
const StatusActionCell: React.FC<StatusActionCellProps> = ({ alert, busy, onChange }) => {
  const current = alert.alertStatus as TrailLeakageAlertStatus;
  const options = NEXT_STATUS_OPTIONS[current] ?? [];
  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-[0.7rem] font-semibold uppercase tracking-wider ${
          STATUS_BADGE_CLASS[current] ?? 'bg-gray-800 text-gray-200'
        }`}
      >
        {alert.alertStatus.replace('_', ' ')}
      </span>
      <select
        aria-label={`Change status for ${alert.primaryBorrowerName || alert.dealId}`}
        disabled={busy || options.length === 0}
        value=""
        onChange={(e) => {
          const next = e.target.value as TrailLeakageAlertStatus;
          if (next) onChange(next);
          e.target.value = '';
        }}
        className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-gray-200 hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="">{busy ? 'Saving…' : 'Change to…'}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {STATUS_LABEL[opt]}
          </option>
        ))}
      </select>
    </div>
  );
};

interface SortHeaderProps {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey | null;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right';
  onSort: (key: SortKey) => void;
}

const SortHeader: React.FC<SortHeaderProps> = ({ label, sortKey, activeKey, dir, align = 'left', onSort }) => {
  const active = activeKey === sortKey;
  return (
    <th className={`px-4 py-3 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider text-xs font-medium hover:text-white ${
          active ? 'text-white' : 'text-gray-400'
        } ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        {label}
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )
        ) : (
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50" />
        )}
      </button>
    </th>
  );
};

const TrailLeakagePage: React.FC = () => {
  const { userData } = useOutletContext<{ userData?: { companyId?: string } }>();
  const companyId = userData?.companyId ?? '';

  const [status, setStatus] = useState<TrailLeakageStatusFilter>('open');
  const { alerts, totals, loading, error, refresh, updateStatus, updatingAlertId } =
    useTrailLeakage({ companyId, status });

  const [actionError, setActionError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('monthsSince');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [brokerFilter, setBrokerFilter] = useState<string>('all');
  const [officeFilter, setOfficeFilter] = useState<string>('all');

  const suspectValueFormatted = useMemo(
    () => formatCurrency(totals.totalSuspectLoanValueCents),
    [totals.totalSuspectLoanValueCents]
  );

  const brokerOptions = useMemo(() => {
    const set = new Set<string>();
    for (const a of alerts) if (a.brokerName) set.add(a.brokerName);
    return Array.from(set).sort((x, y) => x.localeCompare(y));
  }, [alerts]);

  const officeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const a of alerts) if (a.officeName) set.add(a.officeName);
    return Array.from(set).sort((x, y) => x.localeCompare(y));
  }, [alerts]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleStatusChange = async (alertId: string, next: TrailLeakageAlertStatus) => {
    setActionError(null);
    try {
      await updateStatus(alertId, next);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const visibleAlerts = useMemo(() => {
    const filtered = alerts.filter((a) => {
      if (brokerFilter !== 'all' && a.brokerName !== brokerFilter) return false;
      if (officeFilter !== 'all' && a.officeName !== officeFilter) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmpStr = (x: string | null, y: string | null) =>
      (x ?? '').localeCompare(y ?? '', 'en-AU', { sensitivity: 'base' });
    const cmpNum = (x: number | null, y: number | null) =>
      (x ?? Number.NEGATIVE_INFINITY) - (y ?? Number.NEGATIVE_INFINITY);
    const sorted = [...filtered].sort((a, b) => {
      let base = 0;
      switch (sortKey) {
        case 'borrower':
          base = cmpStr(a.primaryBorrowerName, b.primaryBorrowerName);
          break;
        case 'lender':
          base = cmpStr(a.canonicalLender ?? a.lenderName, b.canonicalLender ?? b.lenderName);
          break;
        case 'broker':
          base = cmpStr(a.brokerName, b.brokerName);
          break;
        case 'office':
          base = cmpStr(a.officeName, b.officeName);
          break;
        case 'loanValue':
          base = cmpNum(a.totalLoanAmountCents, b.totalLoanAmountCents);
          break;
        case 'settled':
          base = cmpStr(a.dateSettled, b.dateSettled);
          break;
        case 'monthsSince':
        default:
          base = cmpNum(a.monthsSinceSettlement, b.monthsSinceSettlement);
          break;
      }
      if (base === 0) return a.alertId.localeCompare(b.alertId);
      return base * dir;
    });
    return sorted;
  }, [alerts, brokerFilter, officeFilter, sortKey, sortDir]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={AlertTriangle}
        title="Trail Leakage"
        subtitle="Settled deals in your CRM with no matching trail commission row (settled between 60 days and 6 years ago)"
        badge="Beta"
        onRefresh={refresh}
        isRefreshing={loading}
      />

      <TrailLeakageDefinitionBanner />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="flex items-center gap-2 text-red-400 text-xs uppercase tracking-wider">
            <AlertTriangle className="w-3.5 h-3.5" />
            Open alerts
          </div>
          <div className="mt-2 text-3xl font-bold text-white">{totals.open}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="flex items-center gap-2 text-amber-400 text-xs uppercase tracking-wider">
            <Clock className="w-3.5 h-3.5" />
            Investigating
          </div>
          <div className="mt-2 text-3xl font-bold text-white">{totals.investigating}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-wider">
            <LinkIcon className="w-3.5 h-3.5" />
            Resolved
          </div>
          <div className="mt-2 text-3xl font-bold text-white">{totals.resolved}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="flex items-center gap-2 text-blue-400 text-xs uppercase tracking-wider">
            <DollarSign className="w-3.5 h-3.5" />
            Suspect loan value
          </div>
          <div className="mt-2 text-3xl font-bold text-white">{suspectValueFormatted}</div>
          <p className="text-xs text-gray-500 mt-1">Total of open + investigating alerts</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => {
          const active = tab.key === status;
          return (
            <button
              key={tab.key}
              onClick={() => setStatus(tab.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors border ${
                active
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-gray-900/40 border-gray-700 text-gray-300 hover:text-white hover:border-gray-500'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {(brokerOptions.length > 0 || officeOptions.length > 0) && !loading && alerts.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="tl-broker-filter" className="text-xs uppercase tracking-wider text-gray-400">
              Broker
            </label>
            <select
              id="tl-broker-filter"
              value={brokerFilter}
              onChange={(e) => setBrokerFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-gray-200 hover:border-gray-500"
            >
              <option value="all">All brokers</option>
              {brokerOptions.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="tl-office-filter" className="text-xs uppercase tracking-wider text-gray-400">
              Office
            </label>
            <select
              id="tl-office-filter"
              value={officeFilter}
              onChange={(e) => setOfficeFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-gray-200 hover:border-gray-500"
            >
              <option value="all">All offices</option>
              {officeOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          {(brokerFilter !== 'all' || officeFilter !== 'all') && (
            <button
              type="button"
              onClick={() => {
                setBrokerFilter('all');
                setOfficeFilter('all');
              }}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Clear filters
            </button>
          )}
          <span className="text-xs text-gray-500">
            {visibleAlerts.length} of {alerts.length} shown
          </span>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300">
          {error}
        </div>
      )}

      {actionError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300">
          Could not update alert: {actionError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Loading alerts…
        </div>
      ) : alerts.length === 0 ? (
        <MappingCoverageEmptyState companyId={companyId} status={status} />
      ) : visibleAlerts.length === 0 ? (
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-10 text-center text-gray-400">
          No alerts match the selected broker/office filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60">
              <tr>
                <SortHeader label="Borrower" sortKey="borrower" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Lender" sortKey="lender" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Broker" sortKey="broker" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Office" sortKey="office" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Loan value" sortKey="loanValue" activeKey={sortKey} dir={sortDir} align="right" onSort={handleSort} />
                <SortHeader label="Settled" sortKey="settled" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Months since" sortKey="monthsSince" activeKey={sortKey} dir={sortDir} align="right" onSort={handleSort} />
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {visibleAlerts.map((alert) => (
                <tr key={alert.alertId} className="hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-white">
                    {alert.primaryBorrowerName || <span className="text-gray-500 italic">Name unavailable</span>}
                    <div className="text-[0.7rem] text-gray-500">Deal {alert.dealId}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {alert.canonicalLender || alert.lenderName || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {alert.brokerName || alert.adviserName || (
                      <span className="text-gray-500 italic">Unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{alert.officeName || '—'}</td>
                  <td className="px-4 py-3 text-right text-gray-200">
                    {formatCurrency(alert.totalLoanAmountCents)}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{formatDate(alert.dateSettled)}</td>
                  <td className="px-4 py-3 text-right text-gray-200">
                    {alert.monthsSinceSettlement ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusActionCell
                      alert={alert}
                      busy={updatingAlertId === alert.alertId}
                      onChange={(next) => handleStatusChange(alert.alertId, next)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrailLeakagePage;
